// [WPAR3 2026-08-12] One dependency-free jurisdiction law shared by the admin audit UI,
// server retrieval, unit tests, and the deployed selftest.
export const BRAIN_BASIS_CATEGORIES = Object.freeze([
  "maintenance",
  "tires",
  "payer-wholesale",
  "no-part-number",
  "collision-outside",
  "insurance",
  "goodwill-policy",
  "accessory",
  "recon",
  "warranty-likeness-include",
  "other-CA-law",
]);

export const AR_PRECEDENT_BASIS_CATEGORIES = Object.freeze([
  "maintenance",
  "tires",
  "payer-wholesale",
  "no-part-number",
]);

export const BASIS_CONFIDENCES = Object.freeze(["high", "medium", "low"]);

export function isBasisCategory(value) {
  return BRAIN_BASIS_CATEGORIES.includes(value);
}

// AR has no native brain evidence yet. Its PRECEDENT lane deliberately borrows only
// Blake-approved CA maintenance-family rulings. PRINCIPLES never borrow: a tag approval means
// "the tag is correct", not "this principle is Arkansas law" — an AR principle is one AUTHORED
// with state='AR' (that authoring IS the individual re-approval Blake ruled). [WPAR3-R1 2026-08-12]
export function brainEvidenceScope(states, type = "precedent") {
  const normalized = Array.isArray(states) ? states.map(value => String(value || "").toUpperCase()).filter(Boolean) : [];
  if (normalized.includes("AR") && !(normalized.length === 1 && normalized[0] === "AR")) {
    // [WPAR3-R3 2026-08-12] No mixed-AR evidence rule exists yet — fail LOUD, never degrade silently.
    throw new Error(`No evidence rule for mixed-state scope [${normalized.join(",")}] — AR must be scoped alone`);
  }
  if (normalized.length === 1 && normalized[0] === "AR" && type === "precedent") {
    return { states: ["CA"], requireBasisApproved: true, basisCategories: [...AR_PRECEDENT_BASIS_CATEGORIES] };
  }
  return { states: normalized.length ? normalized : null, requireBasisApproved: false, basisCategories: null };
}

export const JURISDICTION_AUDIT_PROMPT = `You are performing a jurisdiction audit of human-approved classifier knowledge.

For each entry, identify the single deciding basis that best explains why its decision was correct under the law and policy in effect when it was created. Classify the deciding basis—not merely the repair component, keywords, or general subject matter.

For a principle entry (a heuristic rather than a line decision), the deciding basis is the law or doctrine the heuristic encodes; classify it with the same categories.

Allowed basisCategory values:
- maintenance
- tires
- payer-wholesale
- no-part-number
- collision-outside
- insurance
- goodwill-policy
- accessory
- recon
- warranty-likeness-include
- other-CA-law

Category guidance:
- maintenance: routine or scheduled upkeep, service items, wear-item maintenance, or maintenance packages, excluding tire-specific rulings.
- tires: tire replacement, repair, balancing, rotation, road-force work, or tire-specific upkeep.
- payer-wholesale: payer or transaction qualification, including wholesale or non-retail-customer payment.
- no-part-number: the absence or inadequacy of a part number was the deciding basis.
- collision-outside: collision, impact, road hazard, theft, vandalism, rodent, environmental, outside-force, or similar externally caused damage was the deciding basis.
- insurance: insurance involvement or insurer payment was the deciding basis.
- goodwill-policy: goodwill, policy adjustment, dealer/OEM assistance, or similar non-customer-paid treatment was the deciding basis.
- accessory: an accessory, add-on, aftermarket accessory, or modification was the deciding basis.
- recon: reconditioning, inventory preparation, used-car preparation, or recon work was the deciding basis.
- warranty-likeness-include: the entry was included because it described a warranty-like defect, failure, diagnosis, or repair.
- other-CA-law: a California-law deciding basis that does not fit any category above.

Use the supplied entry text, human decision, rationale, and CCC fields together. Do not infer a basis from a component alone when the rationale identifies a different deciding fact. Do not change or reconsider the human decision. Do not invent facts. If more than one category appears relevant, select the category that actually controlled the decision. Use other-CA-law only when no more specific category fits.

Confidence:
- high: the deciding basis is explicit and maps cleanly to one category.
- medium: the basis is strongly implied but not explicit, or two categories are plausible with one better supported.
- low: the record is sparse, contradictory, or genuinely unclear.

If the rationale is missing and the deciding basis cannot be identified from the remaining fields, return confidence "low" and say so in "why" — never guess a basis to avoid a low.

Return ONLY a JSON array with exactly one result per input entry, in the same order:
[
  {
    "id": "<unchanged input id>",
    "basisCategory": "<one allowed value>",
    "confidence": "high|medium|low",
    "why": "<one concise sentence identifying the deciding evidence>"
  }
]`;

